import * as React from 'react';

import { Button } from './button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './sheet';
import { cn } from '../utils/cn';

export interface UserProfileMenuProps {
  avatarLabel?: string;
  username?: string;
  email?: string;
  onLogout?: () => void | Promise<void>;
  description?: string;
  renderContent?: (close: () => void) => React.ReactNode;
}

export function UserProfileMenu({
  avatarLabel = '?',
  username,
  email,
  description = 'Manage your account settings and scopes.',
  onLogout,
  renderContent
}: UserProfileMenuProps) {
  const [open, setOpen] = React.useState(false);

  const close = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogout = React.useCallback(async () => {
    if (onLogout) {
      await onLogout();
    }
    close();
  }, [close, onLogout]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border/60 bg-muted/60 text-sm font-semibold uppercase text-foreground transition hover:bg-muted"
          aria-label="Open profile menu"
        >
          {avatarLabel}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full max-w-sm flex-col gap-6 p-5">
        <SheetHeader>
          <SheetTitle>Your profile</SheetTitle>
          <p className="text-sm text-muted-foreground">{description}</p>
        </SheetHeader>
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
            {avatarLabel}
          </div>
          <div className="space-y-1 text-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Signed in as</p>
            <p className="text-lg font-semibold text-foreground">{username ?? 'User'}</p>
            {email ? <p className="text-sm text-muted-foreground">{email}</p> : null}
          </div>
        </div>
        {renderContent ? <div className="space-y-4">{renderContent(close)}</div> : null}
        {onLogout ? (
          <Button variant="destructive" onClick={handleLogout}>
            Logout
          </Button>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
